import { useEffect, useMemo, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { ApiClientService, useApiClient } from 'libs.react_internal';
/**
 * SSP-4186: cap on how many ids/keys are placed into a single library GET.
 * Mirrors the batch size used by `services/custom_ecoa/src/handlers/migrate.ts`
 * and `apps/ecoa_builder/src/components/activities_dashboard/library_manager.hook.tsx`.
 */
const LIBRARY_BATCH_SIZE = 10;
const chunk = (items, size) => {
    if (size <= 0) {
        return [items];
    }
    const out = [];
    for (let i = 0; i < items.length; i += size) {
        out.push(items.slice(i, i + size));
    }
    return out;
};
const resolveChartId = (ref) => ref.libraryResourceId || ref.id;
const parseVersion = (v) => {
    const n = parseInt(v || '0', 10);
    return Number.isFinite(n) ? n : 0;
};
/**
 * SSP-3756 (dashboards): given the dashboard's chart refs, return the set of
 * chart-ref ids whose underlying chart resource has a newer active PUBLISHED
 * version in the Library.
 *
 * Mirrors `useDatasetVersionCheck` from SSP-3464 (charts) — centralizes a
 * detection that used to live per-card in `DashboardChartCard.checkForNewerVersion`
 * and was plumbed back up via an `onNewerVersionFound` callback. By driving
 * detection directly from the `chartRefs` array, the hook also reacts when
 * the refs are updated (e.g. after Apply on the Review versions modal) so
 * the dashboard's "New versions available" banner clears without a manual
 * page refresh.
 *
 * Status-agnostic: fires for DRAFT, IN_REVIEW, and PUBLISHED dashboards.
 * The Apply handler in the parent branches on dashboard status when deciding
 * between in-place update and clone-with-CREATE_VERSION + archive.
 *
 * Errors per chart are swallowed (that chart simply isn't flagged); a global
 * API failure resolves to an empty set so the dashboard UI never blocks on
 * the check.
 *
 * SSP-4186: reduced from `2N` library GETs (one by-id + one by-key per chart)
 * to at most `ceil(N/10) * 2` chunked list requests: one pass resolves each
 * ref's current `key`+`version`, a second pass fetches all published versions
 * for the union of keys, and the newer-version check runs locally.
 */
export const useChartsWithNewerVersion = (chartRefs) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    const [chartsWithNewerVersions, setChartsWithNewerVersions] = useState(() => new Set());
    const [isChecking, setIsChecking] = useState(false);
    // Stable signature so the effect only re-runs when the set of chart refs
    // (by id + library resource id) actually changes — not on every parent
    // re-render that passes a freshly-allocated `chartRefs` array.
    const refsSignature = useMemo(() => chartRefs.map((ref) => `${ref.id}::${resolveChartId(ref)}`).join('|'), [chartRefs]);
    useEffect(() => {
        if (chartRefs.length === 0) {
            setChartsWithNewerVersions(new Set());
            return;
        }
        let cancelled = false;
        const run = async () => {
            setIsChecking(true);
            try {
                const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
                const flagged = new Set();
                // Step 1 — resolve each ref's current chart resource (for `key` + `version`)
                // in chunked multi-id list calls. A chunk that fails is skipped; refs
                // it would have covered simply won't get flagged, preserving the
                // original per-chart error-swallow semantics.
                const chartIds = [...new Set(chartRefs.map(resolveChartId).filter(Boolean))];
                const currentById = new Map();
                for (const idChunk of chunk(chartIds, LIBRARY_BATCH_SIZE)) {
                    try {
                        const { data } = (await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                            id: idChunk,
                        }));
                        for (const r of data || []) {
                            if (r?.id) {
                                currentById.set(r.id, r);
                            }
                        }
                    }
                    catch {
                        // Skip this id chunk; its refs remain unresolved and won't be flagged.
                    }
                }
                // Step 2 — fetch every published version for the union of resolved keys
                // in chunked multi-key list calls.
                const keys = [
                    ...new Set([...currentById.values()].map((r) => r.key).filter((k) => typeof k === 'string' && !!k)),
                ];
                const publishedByKey = new Map();
                for (const keyChunk of chunk(keys, LIBRARY_BATCH_SIZE)) {
                    try {
                        const { data } = (await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                            key: keyChunk,
                            status: ResourceStatus.PUBLISHED,
                        }));
                        for (const r of data || []) {
                            if (!r?.key) {
                                continue;
                            }
                            const list = publishedByKey.get(r.key);
                            if (list) {
                                list.push(r);
                            }
                            else {
                                publishedByKey.set(r.key, [r]);
                            }
                        }
                    }
                    catch {
                        // Skip this key chunk; refs pointing at these keys won't be flagged.
                    }
                }
                // Step 3 — per ref, compare its current version against its key's
                // published set locally.
                for (const ref of chartRefs) {
                    const chartId = resolveChartId(ref);
                    const current = currentById.get(chartId);
                    if (!current?.key) {
                        continue;
                    }
                    const currentVersion = parseVersion(current.version);
                    const hasNewer = (publishedByKey.get(current.key) || []).some((v) => v.id !== current.id && v.active && parseVersion(v.version) > currentVersion);
                    if (hasNewer) {
                        flagged.add(chartId);
                    }
                }
                if (!cancelled) {
                    setChartsWithNewerVersions(flagged);
                }
            }
            catch {
                if (!cancelled) {
                    setChartsWithNewerVersions(new Set());
                }
            }
            finally {
                if (!cancelled) {
                    setIsChecking(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [refsSignature]);
    return { chartsWithNewerVersions, isChecking };
};
